import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {concat, Observable} from "rxjs";
import {environment} from "../../environments/environment";
import {
  Equipment,
  EquipmentAssignRequest,
  EquipmentBulkUpdateRequest,
  EquipmentCreateUpdateRequest,
  EquipmentGroup,
  EquipmentListResponse,
  EquipmentMoveToRequest,
  EquipmentStatus,
  EquipmentSuggestResponse,
  ItemStatusFilterSelection
} from "./model/equipment";

@Injectable()
export class EquipmentService {
  constructor(private http: HttpClient) {
  }

  list(args: Partial<EquipmentListArgs>): Observable<EquipmentListResponse> {
    const defaultArgs: EquipmentListArgs = {
      group: EquipmentGroup.PROJECT,
      projectIds: [], accountingIds: [], text: "",
      statuses: [], responsible: [], manufacturers: [], serialNumbers: [],
      itemStatuses: [], alerts: [], selection: [], diffFromContract: [],
      notes: [], comments: [],
      includeChildrenProjects: true, page: 0, perPage: 100,
    }
    const a = {...defaultArgs, ...args};
    //
    return this.http.get<EquipmentListResponse>(environment.apiUrl + "/equipment", {
      params: {
        group: a.group,
        projectId: a.projectIds,
        accountingId: a.accountingIds,
        text: a.text,
        status: a.statuses,
        responsible: a.responsible,
        manufacturer: a.manufacturers,
        serialNumber: a.serialNumbers,
        ...a.itemStatuses.reduce((result: any, item) => {
          if (item.selected.length > 0) result[`itemStatus.${item.status}`] = item.selected;
          return result;
        }, {}),
        alerts: a.alerts,
        selection: a.selection,
        diffFromContract: a.diffFromContract,
        notes: a.notes,
        comments: a.comments,
        includeChildrenProjects: a.includeChildrenProjects,
        page: a.page,
        perPage: a.perPage,
      }
    })
  }

  findBySerialNumber(serialNumber: string): Observable<EquipmentListResponse> {
    return this.list({serialNumbers: [serialNumber]})
  }

  suggest(projectId: string): Observable<EquipmentSuggestResponse> {
    return this.http.get<EquipmentSuggestResponse>(environment.apiUrl + "/equipment/suggest", {
      params: {
        projectId: projectId,
      }
    });
  }

  create(projectId: string, request: EquipmentCreateUpdateRequest): Observable<Equipment> {
    return this.http.post<Equipment>(environment.apiUrl + "/equipment?projectId=" + projectId, request)
  }

  delete(id: string): Observable<any> {
    return this.http.delete(environment.apiUrl + "/equipment/" + id)
  }

  update(id: string, request: EquipmentCreateUpdateRequest): Observable<Equipment> {
    return this.http.put<Equipment>(environment.apiUrl + "/equipment/" + id, request)
  }

  bulkUpdate(request: EquipmentBulkUpdateRequest): Observable<any> {
    return this.http.put<Equipment>(environment.apiUrl + "/equipment/bulk", request)
  }

  assign(request: EquipmentAssignRequest): Observable<any> {
    return this.http.post<Equipment>(environment.apiUrl + "/equipment/assign", request);
  }

  moveTo(request: EquipmentMoveToRequest): Observable<any> {
    return this.http.post<Equipment>(environment.apiUrl + "/equipment/move", request)
  }

  copy(equipment: Equipment[], copyToProjectId: string): Observable<any> {
    return concat(
      ...equipment.map(e => {
        const request = {
          manufacturer: e.manufacturer,
          sku: e.sku,
          model: e.model,
          description: e.description,
          hyperlink: e.hyperlink,
          quantity: e.quantity,
          projectCode: (e.projectId == copyToProjectId ? e.projectCode : ""),
          status: EquipmentStatus.PENDING_ORDER,
          group: EquipmentGroup.PROJECT,
          assignedProjectIds: (e.projectId == copyToProjectId ? e.assignedProjectIds : []),
        } as EquipmentCreateUpdateRequest;
        return this.create(copyToProjectId, request);
      })
    );
  }
}

export interface EquipmentListArgs {
  group: EquipmentGroup,
  projectIds: string[],
  accountingIds: string[],
  text: string,
  statuses: string[],
  responsible: string[],
  manufacturers: string[],
  serialNumbers: string[],
  itemStatuses: ItemStatusFilterSelection[],
  alerts: string[],
  selection: string[],
  diffFromContract: string[],
  notes: string[],
  comments: string[],
  includeChildrenProjects: boolean,
  page: number,
  perPage: number,
}
